/**
 * TodoRecordRow — the canonical "saved todo" row used everywhere we
 * display an existing todo in list form:
 *
 *   - Inline todos section on SegmentCard
 *   - Existing todos section inside EditSegmentDialog (rendered by
 *     DraftTodosSection via the render-prop)
 *
 * Look matches PaymentRecordRow: dark card with description on top
 * line, priority + status badges + due-date on the second line, trash
 * icon + right chevron on the right edge. Click row body = edit;
 * click trash = delete. Optional `onToggle` renders a leading
 * checkbox circle for fast completion toggling.
 *
 * Props:
 *   todo                  — todo object
 *   onEdit(todo)          — click handler for row body
 *   onDelete(todo)        — click handler for trash icon
 *   onToggle(id, status)  — optional toggle complete (renders circle)
 *   testId                — base for data-testids
 *   compact               — slightly tighter padding
 */
import { Button } from "@/components/ui/button";
import { ChevronRight, Trash2, CheckCircle, Circle } from "lucide-react";

const PRIORITY_CLASS = {
  high: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  low: "bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-gray-400",
  medium: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
};

const TodoRecordRow = ({
  todo,
  onEdit,
  onDelete,
  onToggle,
  testId,
  compact = false,
}) => {
  const rowId = testId || `todo-row-${todo.id}`;
  const priorityClass = PRIORITY_CLASS[todo.priority] || PRIORITY_CLASS.medium;
  const completed = todo.status === "complete";

  return (
    <div
      onClick={() => onEdit?.(todo)}
      className={`flex items-center justify-between ${compact ? "p-2" : "p-3"} bg-gray-50 dark:bg-slate-900/60 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 cursor-pointer transition-colors border border-gray-200 dark:border-slate-800`}
      data-testid={rowId}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {onToggle && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(todo.id, todo.status);
            }}
            className="flex-shrink-0"
            data-testid={`${rowId}-toggle-btn`}
            aria-label={completed ? "Mark not started" : "Mark complete"}
          >
            {completed ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <Circle className="h-4 w-4 text-gray-400" />
            )}
          </button>
        )}
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-medium ${completed ? "line-through text-gray-400" : "text-gray-900 dark:text-gray-100"} truncate`}>
            {todo.description}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${priorityClass}`}>
              {todo.priority || "medium"}
            </span>
            {todo.due_date && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Due {todo.due_date}{todo.due_time ? ` ${todo.due_time}` : ""}
              </span>
            )}
            {todo.status && todo.status !== "not_started" && (
              <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">· {todo.status.replace(/_/g, " ")}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        {onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(todo);
            }}
            className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
            data-testid={`${rowId}-delete-btn`}
            aria-label="Delete to-do"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
        <ChevronRight className="h-4 w-4 text-gray-400" />
      </div>
    </div>
  );
};

export default TodoRecordRow;
